import { Link } from "react-router-dom";
import type { Anime } from "@/mocks/anime";
import { formatRating, formatViews } from "@/utils/format";
import { useImagePreview } from "@/components/feature/ImagePreviewProvider";

interface AnimeCardProps {
  anime: Anime;
  /** 展示简介文本 */
  showDescription?: boolean;
  /** 展示更新状态角标 */
  showStatus?: boolean;
}

export default function AnimeCard({ anime, showDescription = false, showStatus = true }: AnimeCardProps) {
  const { openPreview } = useImagePreview();

  return (
    <article className="group flex flex-col overflow-hidden rounded-lg border border-background-200/70 bg-background-50 transition-colors hover:border-primary-200">
      <div className="relative w-full overflow-hidden" style={{ aspectRatio: "3 / 4" }}>
        <Link to={`/detail/${anime.id}`} aria-label={`查看《${anime.title}》详情`}>
          <img
            src={anime.cover}
            alt={`${anime.title} 动漫封面`}
            title={`${anime.title} 樱花动漫在线观看`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
          <span className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-transparent opacity-80" />
        </Link>

        <span className="absolute left-2 top-2 flex items-center gap-1 rounded-md bg-foreground-950/70 px-2 py-1 text-xs font-semibold text-background-50 backdrop-blur-sm">
          <i className="ri-star-fill text-accent-400" />
          {formatRating(anime.rating)}
        </span>

        <button
          type="button"
          onClick={() => openPreview({ src: anime.cover, title: anime.title })}
          className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-md bg-background-50/85 text-foreground-800 opacity-0 backdrop-blur-sm transition-opacity hover:bg-background-50 group-hover:opacity-100"
          aria-label={`放大预览《${anime.title}》封面`}
        >
          <i className="ri-zoom-in-line" />
        </button>

        {showStatus && (
          <span className="absolute bottom-2 left-2 rounded-md bg-primary-500/95 px-2 py-1 text-xs font-medium text-background-50">
            {anime.status}
          </span>
        )}
      </div>

      <div className="flex flex-1 flex-col p-3">
        <h3 className="truncate font-heading text-base font-bold text-foreground-950" title={anime.title}>
          <Link to={`/detail/${anime.id}`} className="transition-colors hover:text-primary-700">
            {anime.title}
          </Link>
        </h3>

        <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-foreground-500">
          <span className="whitespace-nowrap">{anime.region}</span>
          <span className="text-background-300">·</span>
          <span className="whitespace-nowrap">{anime.year}</span>
          <span className="text-background-300">·</span>
          <span className="whitespace-nowrap">{anime.episodes} 集</span>
        </div>

        {showDescription && (
          <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-foreground-600">
            {anime.description}
          </p>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          {anime.genres.slice(0, 2).map((genre) => (
            <Link
              key={genre}
              to={`/category?genre=${encodeURIComponent(genre)}`}
              className="rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] text-secondary-900 transition-colors hover:bg-secondary-200"
            >
              {genre}
            </Link>
          ))}
        </div>

        <div className="mt-3 flex items-center justify-between border-t border-background-200/70 pt-2.5 text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-play-circle-line text-sm text-primary-500" />
            {formatViews(anime.views)}
          </span>
          <span className="flex items-center gap-1 text-foreground-400">
            <i className="ri-time-line text-sm" />
            近期更新
          </span>
        </div>
      </div>
    </article>
  );
}